import axios, {
  AxiosError,
  isAxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import kc from '@/lib/keycloak';

// ---- Typed API error helpers ----------------------------------------------
// The axios response interceptor still rejects with the underlying
// AxiosError so existing call sites keep working. Components that want to
// branch on status (auth gates, validation toasts, etc.) should use
// `apiStatus(e)` or `isApiStatus(e, 401)` instead of regex-matching messages.

/** HTTP status from any error, or undefined if it wasn't an HTTP response. */
export function apiStatus(err: unknown): number | undefined {
  if (isAxiosError(err)) return err.response?.status;
  return undefined;
}

/** True iff the error is an HTTP response with one of the given statuses. */
export function isApiStatus(err: unknown, ...statuses: number[]): boolean {
  const s = apiStatus(err);
  return s !== undefined && statuses.includes(s);
}

/**
 * Pulls a human-readable error message in this order:
 * 1) the backend's response body if it contains `message` or `error`
 * 2) the axios error message
 * 3) "Unexpected error"
 * Use this when rendering toasts so we surface the server's intent.
 */
export function apiMessage(err: unknown, fallback = 'Unexpected error'): string {
  if (isAxiosError(err)) {
    const body = err.response?.data;
    if (body && typeof body === 'object') {
      const b = body as Record<string, unknown>;
      if (typeof b.message === 'string') return b.message;
      if (typeof b.error === 'string') return b.error;
    }
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

// In dev, Vite proxies `/v1` and `/actuator` to the Spring Boot app.
// In prod the SPA is served from `pulse.app` and calls `api.pulse.app`.
const baseURL = import.meta.env.VITE_API_BASE ?? '';

export const api = axios.create({
  baseURL,
  withCredentials: false,
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (kc.authenticated && kc.token) {
    // Ensure we send a token that has at least 5s left. updateToken is a no-op
    // when the token is fresh enough.
    try {
      await kc.updateToken(5);
    } catch {
      // fall through — request will likely 401 and the response interceptor
      // will redirect to login.
    }
    config.headers.set?.('Authorization', `Bearer ${kc.token}`);
  }
  return config;
});

// Marker so we only retry once.
type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

api.interceptors.response.use(
  (res) => {
    // Guard against the SPA's index.html being returned for an API path
    // (e.g. when VITE_API_BASE is empty in a deployed build). Without this,
    // callers receive HTML where they expect JSON and downstream parsers
    // (e.g. Array.prototype.reduce on a string) crash deep in the tree.
    const ct = res.headers?.['content-type'];
    if (typeof ct === 'string' && ct.includes('text/html')) {
      return Promise.reject(
        Object.assign(new Error('API returned HTML — check VITE_API_BASE'), {
          response: res,
        }),
      );
    }
    return res;
  },
  async (err: AxiosError) => {
    const original = err.config as RetryConfig | undefined;
    if (err.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      try {
        const refreshed = await kc.updateToken(5);
        if (refreshed || kc.authenticated) {
          if (kc.token && original.headers) {
            original.headers.Authorization = `Bearer ${kc.token}`;
          }
          return api(original);
        }
      } catch {
        // fall through to login redirect
      }
      kc.login();
    }
    return Promise.reject(err);
  },
);
